import React, { useEffect, useMemo, useState } from "react";
import { endpoints, API } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Sparkles, AlertTriangle, HelpCircle, ExternalLink, Info, Wand2 } from "lucide-react";
import AuthenticityBadge, { AUTHENTICITY_META } from "@/components/AuthenticityBadge";
import RitualReportsPanel from "@/components/RitualReportsPanel";
import SharedExperienceModerationPanel from "@/components/SharedExperienceModerationPanel";
import { N8 } from "@/components/BrandMark";

/**
 * Moderation — where a moderator reviews before/after photos for signs of
 * heavy editing so users see real results, not exaggerated ones. Every
 * decision writes an `authenticity` block onto the community post and the
 * feed picks it up as a chip on the post card.
 *
 * The moderator has four choices, each with a color-coded intent:
 *   - Verified real  (moss)       — natural or minimally edited
 *   - Light edits    (clay)       — filters/retouching, still honest
 *   - Heavy edits    (terracotta) — significantly altered
 *   - Under review   (muted)      — send back to the queue
 */
const DECISIONS = [
  { status: "verified_real", label: "Verified real", Icon: ShieldCheck, tone: "moss" },
  { status: "light_edits",   label: "Light edits",   Icon: Sparkles,     tone: "clay" },
  { status: "heavy_edits",   label: "Heavy edits",   Icon: AlertTriangle, tone: "terracotta" },
  { status: "pending",       label: "Send back",     Icon: HelpCircle,   tone: "muted" },
];

const CUES = [
  "Skin texture — does it look uniformly airbrushed or naturally varied?",
  "Lighting — same time of day and light source in both frames?",
  "Angle & distance — head tilt, framing, and lens compression consistent?",
  "Edges — any unnatural warping around jawline, waistline, or hair?",
  "Color grade — heavy warm/cool shifts, or matched skin tones?",
];

export default function Moderation() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ items: [], counts: { pending: 0, reviewed: 0 } });
  const [loading, setLoading] = useState(true);

  const load = async (nextTab = tab) => {
    setLoading(true);
    try {
      const { data: d } = await endpoints.moderationQueue(nextTab);
      setData(d);
    } catch {
      toast.error("Couldn't load the moderation queue.");
    }
    setLoading(false);
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const tabs = useMemo(() => [
    { key: "pending",  label: "Pending",  count: data.counts.pending },
    { key: "reviewed", label: "Reviewed", count: data.counts.reviewed },
    { key: "all",      label: "All",      count: data.counts.pending + data.counts.reviewed },
  ], [data.counts]);

  const onDecided = (post_id, authenticity) => {
    // Optimistically update the current tab so the row reflects immediately;
    // then reload counts in the background so tab pills stay accurate.
    setData((prev) => {
      const items = prev.items.map((it) =>
        it.id === post_id ? { ...it, authenticity } : it,
      );
      // If we're on the "pending" tab and this decision moves it out, drop it.
      const filtered = tab === "pending" && authenticity.status !== "pending"
        ? items.filter((it) => it.id !== post_id)
        : items;
      return { ...prev, items: filtered };
    });
    load(tab);
  };

  return (
    <div className="space-y-10" data-testid="moderation-page">
      <header className="reveal">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
          Transparency
        </div>
        <h1 className="font-display text-4xl lg:text-5xl mt-3 tracking-tight leading-tight">
          <span className="text-[hsl(var(--clay))]">Real results,</span>{" "}
          <span className="italic text-[hsl(var(--terracotta))]">not hype.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-[hsl(var(--muted-foreground))] leading-relaxed">
          Review Becoming Photos — then &amp; now — for signs of heavy editing or augmentation. The
          badge you stamp shows up on the post so members trust what they see.
        </p>
      </header>

      <section
        className="p-5 md:p-6 rounded-md bg-[hsl(var(--sand))]/40 border hairline"
        data-testid="moderation-cues"
      >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">
          <Info size={12} /> What to look for
        </div>
        <ul className="mt-3 grid md:grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-[hsl(var(--fg))]/85">
          {CUES.map((c) => (
            <li key={c} className="leading-relaxed">
              <span className="text-[hsl(var(--muted-foreground))] mr-1.5">•</span>{c}
            </li>
          ))}
        </ul>
      </section>

      <div className="flex items-center gap-2 flex-wrap" data-testid="moderation-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            aria-pressed={tab === t.key}
            data-testid={`moderation-tab-${t.key}`}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border hairline tab-link inline-flex items-center gap-2 ${
              tab === t.key
                ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))] border-transparent"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
            }`}
          >
            {t.label}
            <span className="opacity-70 tabular-nums normal-case">{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="moderation-loading">
          Loading the queue…
        </div>
      ) : data.items.length === 0 ? (
        <div
          className="p-10 text-center rounded-md bg-[hsl(var(--paper))] border hairline"
          data-testid="moderation-empty"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
            Nothing to review
          </div>
          <p className="mt-2 font-display text-xl italic">
            The queue is clear. When members share before &amp; after photos, they&apos;ll land here.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {data.items.map((post) => (
            <ReviewCard key={post.id} post={post} onDecided={onDecided} />
          ))}
        </div>
      )}

      <div className="pt-8 border-t hairline">
        <RitualReportsPanel />
      </div>

      {/* Shared Experiences moderation lives inside the same dashboard so
          moderators work from one surface rather than hopping between
          separate review tools. */}
      <SharedExperienceModerationPanel />
    </div>
  );
}

function ReviewCard({ post, onDecided }) {
  const [note, setNote] = useState(post.authenticity?.note || "");
  const [busy, setBusy] = useState(null);
  const [aiAssist, setAiAssist] = useState(post.authenticity_ai || null);
  const [aiBusy, setAiBusy] = useState(false);

  const decide = async (status) => {
    setBusy(status);
    try {
      const { data } = await endpoints.setPostAuthenticity(post.id, { status, note: note.trim() });
      toast.success(`Marked ${AUTHENTICITY_META[status]?.label || status}.`);
      onDecided(post.id, data.authenticity);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save decision.");
    }
    setBusy(null);
  };

  const askAssist = async () => {
    setAiBusy(true);
    try {
      const { data } = await endpoints.moderationAiAssist(post.id);
      setAiAssist(data);
      if (data.suggested_status && data.suggested_status !== "pending") {
        toast.success(`N8 suggests: ${AUTHENTICITY_META[data.suggested_status]?.label}`);
      } else {
        toast.success("N8's first-read is in.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "N8's read didn't come through.");
    }
    setAiBusy(false);
  };

  return (
    <article
      className="border hairline rounded-md bg-[hsl(var(--paper))] overflow-hidden"
      data-testid={`review-card-${post.id}`}
    >
      <div className="p-5 md:p-6 flex items-center justify-between gap-4 flex-wrap border-b hairline">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
            {post.author} · {new Date(post.created_at).toLocaleDateString()}
            {post.is_win && <span className="ml-2 text-[hsl(var(--terracotta))]">win</span>}
          </div>
          <p className="mt-1.5 leading-relaxed text-[hsl(var(--fg))]">
            {post.content || <span className="italic text-[hsl(var(--muted-foreground))]">No caption.</span>}
          </p>
          {post.goal_tags?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {post.goal_tags.map((g) => (
                <span
                  key={g}
                  className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--sand))]/60 text-[hsl(var(--muted-foreground))]"
                >
                  {g}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="shrink-0">
          <AuthenticityBadge authenticity={post.authenticity} size="lg" />
        </div>
      </div>

      <div className="p-5 md:p-6 grid sm:grid-cols-2 gap-4">
        <PhotoTile label="Then" path={post.photo_before_path} testid={`review-photo-before-${post.id}`} />
        <PhotoTile label="Now"  path={post.photo_after_path}  testid={`review-photo-after-${post.id}`} />
      </div>

      <div className="p-5 md:p-6 pt-0 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
            <N8 />&apos;s first-read
          </div>
          <button
            type="button"
            onClick={askAssist}
            disabled={aiBusy}
            data-testid={`review-ai-assist-${post.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.2em] border hairline tab-link ${
              aiBusy
                ? "opacity-60 cursor-wait"
                : "text-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/10"
            }`}
          >
            <Wand2 size={11} />
            {aiBusy ? <><N8 /> is reading…</> : (aiAssist ? "Refresh" : <>Ask <N8 /> for a first-read</>)}
          </button>
        </div>

        {aiAssist ? (
          <div
            className="p-4 rounded-md bg-[hsl(var(--sand))]/40 border-l-2 border-[hsl(var(--terracotta))]"
            data-testid={`review-ai-assist-panel-${post.id}`}
          >
            {aiAssist.first_read && (
              <p className="text-sm text-[hsl(var(--fg))] leading-relaxed" data-testid={`review-ai-first-read-${post.id}`}>
                {aiAssist.first_read}
              </p>
            )}
            {aiAssist.flags?.length > 0 && (
              <div className="mt-3" data-testid={`review-ai-flags-${post.id}`}>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                  Check these in the photos
                </div>
                <ul className="mt-1.5 space-y-1">
                  {aiAssist.flags.map((f, i) => (
                    <li
                      key={i}
                      className="text-[13px] text-[hsl(var(--fg))]/85 leading-relaxed"
                      data-testid={`review-ai-flag-${post.id}-${i}`}
                    >
                      <span className="text-[hsl(var(--terracotta))] mr-1.5">•</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {aiAssist.suggested_status && aiAssist.suggested_status !== "pending" && (
              <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]" data-testid={`review-ai-suggested-${post.id}`}>
                <N8 /> suggests: {AUTHENTICITY_META[aiAssist.suggested_status]?.label || aiAssist.suggested_status}
                <span className="normal-case tracking-normal text-[hsl(var(--muted-foreground))] italic ml-2">
                  (your call is final)
                </span>
              </div>
            )}
            <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]/70" data-testid={`review-ai-vision-status-${post.id}`}>
              {aiAssist.saw_photos === false
                ? "Text-only pass — image fetch failed, moderator eyes needed"
                : <><N8 /> read the two photos directly · your call is final</>}
            </div>
          </div>
        ) : (
          <p className="text-[13px] text-[hsl(var(--muted-foreground))] italic">
            Ask <N8 /> to actually look at the two photos and share what stands out — lighting, texture, framing, edges — so you can review them with a second pair of eyes.
          </p>
        )}

        <div>
          <label
            htmlFor={`note-${post.id}`}
            className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]"
          >
            Moderator note (shown as tooltip on the badge)
          </label>
          <textarea
            id={`note-${post.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional — what did you notice? e.g., 'Consistent lighting; minimal filter.'"
            data-testid={`review-note-${post.id}`}
            rows={2}
            maxLength={400}
            className="mt-2 w-full bg-transparent border hairline rounded-md p-3 text-sm leading-relaxed focus:outline-none focus:border-[hsl(var(--clay))]/50"
          />
        </div>

        <div className="flex flex-wrap gap-2" data-testid={`review-actions-${post.id}`}>
          {DECISIONS.map(({ status, label, Icon, tone }) => {
            const active = post.authenticity?.status === status;
            const busyThis = busy === status;
            const disabled = busy !== null;
            const color =
              tone === "muted" ? "hsl(var(--muted-foreground))" : `hsl(var(--${tone}))`;
            return (
              <button
                key={status}
                type="button"
                disabled={disabled}
                onClick={() => decide(status)}
                data-testid={`review-decide-${status}-${post.id}`}
                aria-pressed={active}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs uppercase tracking-[0.18em] tab-link border hairline ${
                  active ? "text-[hsl(var(--paper))]" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
                } ${disabled ? "opacity-60 cursor-wait" : ""}`}
                style={active ? { backgroundColor: color, borderColor: "transparent" } : {}}
              >
                <Icon size={12} />
                {busyThis ? "Saving…" : label}
              </button>
            );
          })}
        </div>

        {post.authenticity?.reviewer && (
          <div className="text-[11px] text-[hsl(var(--muted-foreground))]" data-testid={`review-meta-${post.id}`}>
            Last reviewed by <span className="font-medium text-[hsl(var(--fg))]">{post.authenticity.reviewer}</span>
            {" "}on {new Date(post.authenticity.reviewed_at).toLocaleString()}
          </div>
        )}
      </div>
    </article>
  );
}

function PhotoTile({ label, path, testid }) {
  const href = path ? `${API}/files/${path}` : null;
  return (
    <div className="relative rounded-md overflow-hidden border hairline bg-[hsl(var(--sand))]/40" data-testid={testid}>
      <div className="absolute top-2 left-2 z-10 text-[10px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-full bg-[hsl(var(--fg))]/80 text-[hsl(var(--paper))]">
        {label}
      </div>
      {href && (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="absolute top-2 right-2 z-10 text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--paper))]/80 text-[hsl(var(--fg))] hover:bg-[hsl(var(--paper))] tab-link"
          data-testid={`${testid}-open`}
        >
          Open <ExternalLink size={9} />
        </a>
      )}
      {href ? (
        <img
          src={href}
          alt={`${label} photo`}
          className="w-full aspect-[4/5] object-cover"
          onError={(e) => { e.currentTarget.style.opacity = "0.35"; }}
        />
      ) : (
        <div className="w-full aspect-[4/5] grid place-items-center text-[hsl(var(--muted-foreground))] text-sm">
          No {label.toLowerCase()} photo
        </div>
      )}
    </div>
  );
}
